import React, { useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Button } from '@dopomogai/ui';
import { HeroBackground } from './HeroBackground';
import { HeroVisual } from './HeroVisual';
import { IntegrationsMarquee, type IntegrationItem } from './IntegrationsMarquee';
import PillStat, { type PillAccent } from './PillStat';
import CornerSticker from './CornerSticker';

export type FeatureStatItem = {
  text: string;
  accent?: PillAccent;
  icon?: 'bolt' | 'globe' | 'share' | 'refresh' | 'shield';
};

export type HomePageHeroProps = {
  platformBadge?: string;
  customTitle?: React.ReactNode;
  title?: string; 
  subtitle?: string;
  primaryCta?: { label?: string; actionId: string };
  secondaryCta?: { label?: string; actionId: string };
  onAction: (actionId: string, payload?: any) => void;
  integrationsItems?: IntegrationItem[];
  className?: string;
  
  heroVisual?: React.ReactNode;
  ethicalStickerText?: string;
  featureStats?: FeatureStatItem[];

  // FIX: Added missing props to match usage in HomePageComposite
  backgroundVariant?: 'grid-blobs' | 'neo-grid';
  backgroundIntensity?: 'subtle' | 'bold';
  integrationsLineText?: string;
};

export const HomePageHero: React.FC<HomePageHeroProps> = ({
  platformBadge = "System Operational // Cohort Open",
  customTitle, title, subtitle,
  primaryCta, secondaryCta, onAction,
  integrationsItems = [],
  className,
  
  heroVisual,
  ethicalStickerText = "3 Partner Slots Left",
  featureStats,

  // Destructure new props (unused background props are accepted to clear TS errors)
  backgroundVariant,
  backgroundIntensity,
  integrationsLineText = "We integrate with your tools & processes",
}) => {
  const { t } = useTranslation('homepage');
  const reduce = useReducedMotion();
  const heroRef = useRef<HTMLDivElement | null>(null);

  // Default Copy
  const resolvedTitle = customTitle || (title ? title : (
    <>
      DON'T RENT AI.<br />
      <span className="text-emerald-600">OWN THE WORKFORCE.</span>
    </>
  ));
  
  const resolvedSubtitle = subtitle || "Stop hiring humans to copy-paste data. We engineer your private, semi-autonomous AI infrastructure—integrated directly into your team's existing workflows.";

  // Default Stats if not provided
  const resolvedStats = featureStats || [
    { text: "Private & Sovereign", accent: "slate" as PillAccent, icon: "shield" },
    { text: "You Own the Asset", accent: "emerald" as PillAccent, icon: "bolt" },
    { text: "Human-in-the-Loop", accent: "blue" as PillAccent, icon: "share" }
  ];

  return (
    <section className={clsx(
      'relative overflow-hidden flex flex-col min-h-[90vh] pt-12',
      'bg-slate-900',
      className
    )}>
        
      {/* 2. THE BLUEPRINT GRID BACKGROUND */}
      <HeroBackground />

      {/* 3. MAIN CONTENT CONTAINER (Centered) */}
      <div ref={heroRef} className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8 flex-grow flex flex-col justify-center">
        
        {/* THE WHITE CARD */}
        <motion.div
          className="relative border-2 border-black rounded-3xl bg-white p-6 md:p-10 lg:p-12 shadow-[8px_8px_0_0_rgba(0,0,0,1)]"
          initial={reduce ? undefined : { opacity: 0, y: 20 }}
          animate={reduce ? undefined : { opacity: 1, y: 0 }}
          transition={reduce ? undefined : { duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          
          {/* Top Badge */}
          <div className="absolute -top-5 left-1/2 -translate-x-1/2 md:left-12 md:translate-x-0 z-20">
             <div className="bg-slate-900 text-white px-5 py-2 rounded-full border-2 border-white shadow-md flex items-center gap-3">
               <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500 border border-emerald-900"></span>
                </span>
               <span className="text-[10px] md:text-xs font-bold uppercase tracking-widest font-mono leading-none mt-0.5">
                  {platformBadge}
               </span>
             </div>
          </div>

          {/* Corner Sticker */}
          <CornerSticker 
            text={ethicalStickerText} 
            position="top-right" 
            offsetX={20} 
            offsetY={-20} 
            angleDeg={6} 
            className="hidden lg:block z-30 hover:scale-110 transition-transform duration-200 cursor-default" 
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center mt-4">
            
            {/* LEFT COLUMN: TEXT */}
            <div className="flex flex-col h-full justify-center relative z-10 text-center lg:text-left order-2 lg:order-1">
              
              <div className="mb-6">
                 <h1 className="font-black text-slate-900 leading-[1.0] text-4xl md:text-6xl lg:text-7xl tracking-tight">
                    {resolvedTitle}
                 </h1>
              </div>

              {/* PILL STATS (Dynamic) */}
              <div className="mb-8 flex flex-wrap justify-center lg:justify-start gap-3">
                {resolvedStats.map((stat, i) => (
                    <PillStat 
                        key={i}
                        text={stat.text} 
                        accent={stat.accent} 
                        icon={stat.icon as any} 
                    />
                ))}
              </div>

              {/* SUBTITLE */}
              <p className="text-slate-600 text-lg md:text-xl leading-relaxed max-w-xl font-medium mx-auto lg:mx-0">
                {resolvedSubtitle}
              </p>

              {/* CTAs */}
              <div className="mt-10 relative flex flex-col sm:flex-row gap-4 items-center justify-center lg:justify-start">
                {primaryCta && (
                  <div className="relative group w-full sm:w-auto">
                      <div className="absolute -inset-1 bg-emerald-400 rounded-xl blur opacity-20 group-hover:opacity-40 transition duration-200"></div>
                      <Button 
                          size="xl" 
                          variant="primary" 
                          fullWidth
                          text={primaryCta.label} 
                          onClick={() => onAction(primaryCta.actionId)} 
                          className="relative shadow-lg border-2 border-black py-4 text-lg"
                      />
                  </div>
                )}
                {secondaryCta && (
                  <Button 
                      size="xl" 
                      variant="ghost" 
                      text={secondaryCta.label} 
                      onClick={() => onAction(secondaryCta.actionId)} 
                      className="hidden sm:flex border-2 border-transparent hover:border-slate-200"
                  />
                )}
              </div>
            </div>

            {/* RIGHT COLUMN: VISUAL */}
            <div className="relative flex items-center justify-center h-full order-1 lg:order-2">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gradient-to-tr from-emerald-100 via-blue-50 to-violet-100 rounded-full blur-[60px] -z-10 opacity-60" />
                <div className="relative w-full max-w-[600px]"> 
                    {/* Use prop if provided, else fallback to default visual */}
                    {heroVisual || <HeroVisual />}
                </div>
            </div>

          </div>
        </motion.div>

      </div>

      {/* 4. FULL-WIDTH INTEGRATIONS STRIP */}
      {integrationsItems?.length > 0 && (
          <div className="relative z-10 w-full mt-auto pt-10 pb-12">
               {/* Label above the strip */}
               <motion.div 
                 className="text-center mb-6"
                 initial={{ opacity: 0 }}
                 animate={{ opacity: 1 }}
                 transition={{ delay: 0.8 }}
               >
                   <p className="text-white/60 text-xs md:text-sm font-bold uppercase tracking-[0.2em] font-mono">
                      {integrationsLineText}
                   </p>
               </motion.div>

               {/* Full width marquee */}
               <motion.div 
                 initial={{ opacity: 0 }}
                 animate={{ opacity: 1 }}
                 transition={{ delay: 0.5, duration: 0.8 }}
               >
                 <IntegrationsMarquee 
                    items={integrationsItems} 
                    isFullWidth={true}
                    speedSec={30}
                    className="" 
                 />
               </motion.div>
          </div>
      )}

    </section>
  );
};

export default HomePageHero;